import React from 'react'
import { IconButton, InputAdornment, Typography, useMediaQuery } from '@mui/material'
import { DateTime } from 'luxon'
import { DateRangeIcon, PickersShortcutsItem, SingleInputDateRangeField } from '@mui/x-date-pickers-pro'
import { DateRangePicker } from '~/components/common/DateRangePicker'
import { colorPalette } from '~/constants/styles/colors'
import { validateReportDates } from '~/lib/feature/report'
import { theme } from '~/lib/theme'

type SingleInputDateRangePickerProps = {
  value: [DateTime | null, DateTime | null]
  setStartDate: (date: DateTime | null) => void
  setEndDate: (date: DateTime | null) => void
}

export const SingleInputDateRangePicker: React.FC<SingleInputDateRangePickerProps> = ({
  value,
  setStartDate,
  setEndDate,
}) => {
  const [errorMessages, setErrorMessages] = React.useState<string>('')
  const [open, setOpen] = React.useState(false)
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const handleChange = (chosenDate: [DateTime | null, DateTime | null]) => {
    setErrorMessages('')
    const [chosenStartDate, chosenEndDate] = chosenDate
    const previousEndDate = value[1]

    let targetEndDate = chosenEndDate

    // モバイル対策: 連打していると本来選べないはずの開始日 > 終了日が選べてしまう場合があるが、
    // その際にhandleChangeで受け取るendDateがnullに置き換えられてしまい(MUIの仕様？)、
    // 画面上のendDateの表示（終了日がnullではなく選べているように見える）と乖離が起きてしまうので正しい値が入っているpreviousEndDate(value[1])の方を正とする
    if (chosenStartDate && chosenEndDate === null && previousEndDate) {
      targetEndDate = previousEndDate
    }

    const errorMessage = validateReportDates(chosenStartDate, targetEndDate).errorMessage
    if (errorMessage) {
      setStartDate(chosenStartDate)
      setEndDate(targetEndDate)
      setErrorMessages(errorMessage)
      return
    }

    setStartDate(chosenStartDate)
    setEndDate(targetEndDate)
  }

  return (
    <DateRangePicker
      singleInput
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      slots={{ field: SingleInputDateRangeField }}
      slotProps={{
        shortcuts: {
          items: isMobile ? [] : dateRangePresets,
        },
        textField: {
          InputProps: {
            startAdornment: isMobile ? null : (
              <InputAdornment position="start">
                <Typography variant="bodyM" color={colorPalette.text.primary}>
                  {getDaysDifference(value[0], value[1])}
                </Typography>
              </InputAdornment>
            ),
            endAdornment: isMobile ? null : (
              <InputAdornment position="end">
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation()
                    setOpen(true)
                  }}
                >
                  <DateRangeIcon />
                </IconButton>
              </InputAdornment>
            ),
          },
          sx: {
            '& .MuiPickersInputBase-sectionsContainer': {
              padding: '8.5px 0',
            },
            '& .MuiPickersSectionList-root': {
              width: '100%',
            },
          },
          error: !!errorMessages,
          helperText: errorMessages,
          onClick: () => setOpen(true),
        },
      }}
      minDate={DateTime.now().minus({ years: 2 })}
      maxDate={DateTime.now()}
      onChange={handleChange}
      value={value}
    />
  )
}

const getDaysDifference = (startDate: DateTime | null, endDate: DateTime | null): string => {
  if (startDate?.isValid && endDate?.isValid) {
    const diff = Math.floor(endDate.diff(startDate, 'days').days)
    return `過去${diff}日間：`
  }
  return ''
}

const dateRangePresets: PickersShortcutsItem<[DateTime | null, DateTime | null]>[] = [
  {
    label: '今週',
    getValue: () => {
      const today = DateTime.local()
      const startOfWeek = today.startOf('week')
      return [startOfWeek, today]
    },
  },
  {
    label: '先週',
    getValue: () => {
      const today = DateTime.local()
      const prevWeek = today.minus({ weeks: 1 })
      const startOfLastWeek = prevWeek.startOf('week')
      const endOfLastWeek = prevWeek.endOf('week')
      return [startOfLastWeek, endOfLastWeek]
    },
  },
  {
    label: '今月',
    getValue: () => {
      const today = DateTime.local()
      const startOfMonth = today.startOf('month')
      return [startOfMonth, today]
    },
  },
  {
    label: '先月',
    getValue: () => {
      const today = DateTime.local()
      const startOfLastMonth = today.minus({ months: 1 }).startOf('month')
      const endOfLastMonth = today.minus({ months: 1 }).endOf('month')
      return [startOfLastMonth, endOfLastMonth]
    },
  },
  {
    label: '３ヶ月',
    getValue: () => {
      const today = DateTime.local()
      const ninetyDaysAgo = today.minus({ days: 90 })
      return [ninetyDaysAgo, today]
    },
  },
  {
    label: 'リセット',
    getValue: () => {
      return [null, null]
    },
  },
]
